/**
 * /debug/failure-capture ✦ iter208
 * ─────────────────────────────────────────────────────────────
 * Operational failure intelligence surface. Lets an operator
 * intentionally provoke a failure archetype in the wild and
 * capture its canonical signature for the EventDrop failure
 * library.
 *
 * Per Craig (iter208 directive):
 *   "model venue chaos, guest behaviour, hardware stress, mobile
 *    ecosystem instability — not developer failures. Operator
 *    manually classifies for now. No opaque ML. Remain explainable."
 *
 * Workflow:
 *   1. Operator picks the camera + audio profiles to capture
 *      against (booth, lens, guestbook, etc.).
 *   2. Operator picks a failure class from the frozen vocabulary
 *      and a severity (minor / major / critical).
 *   3. Operator triggers `Acquire ✦` to open camera + mic.
 *   4. Operator induces the failure condition physically (cover
 *      the lens, unplug headphones, kill Wi-Fi, etc.) — the live
 *      passive-detector readout updates in real time.
 *   5. Operator authors `reproductionSteps` + `operatorNotes`.
 *   6. `Capture failure ✦` stores the bundle locally.
 *   7. `Classify current state ✦` runs the rule-based classifier
 *      against the live signature — useful for verifying captured
 *      archetypes match the auto-classifier without surprises.
 *
 * Hidden from production navigation. Direct URL access only.
 */
import React, { useEffect, useState, useMemo } from 'react';
import { useBoothCameraPipeline } from '../../hooks/useBoothCameraPipeline';
import { useParticipationAudioPipeline } from '../../hooks/useParticipationAudioPipeline';
import { BOOTH_CAMERA_PROFILES } from '../../lib/booth-os/boothCameraProfiles';
import { PARTICIPATION_AUDIO_PROFILES } from '../../lib/booth-os/participationAudioProfiles';
import {
  FAILURE_CLASS_LIST, SEVERITY_LIST,
  detectLuminance, buildSignature, classifyFailure,
} from '../../lib/booth-os/failureSignatures';
import {
  FAILURE_REHEARSAL_PACK_LIST,
} from '../../lib/booth-os/failureRehearsalPacks';
import {
  saveFailure, listFailures, deleteFailure,
  deviceFingerprint, PIPELINE_VERSION,
  archaeology,
  downloadGolden,
} from '../../lib/booth-os/goldenReferences';
import { captureConditions as captureSoakConditions, snapshot as soakSnapshot } from '../../lib/booth-os/soakTelemetry';

const CAMERA_PROFILES = Object.keys(BOOTH_CAMERA_PROFILES);
const AUDIO_PROFILES = Object.keys(PARTICIPATION_AUDIO_PROFILES);

const IVORY = '#f8e8c8';
const IVORY_FADED = 'rgba(248,232,200,0.55)';
const PANEL_BG = 'rgba(0,0,0,0.4)';
const GOLD_BORDER = '1px solid rgba(212,175,94,0.18)';

const Pill = ({ active, onClick, children, testId, tone }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    style={{
      padding: '6px 12px',
      borderRadius: 999,
      border: `1px solid ${tone === 'danger' ? 'rgba(224,57,42,0.45)' : 'rgba(212,175,94,0.35)'}`,
      background: active
        ? (tone === 'danger' ? 'rgba(224,57,42,0.18)' : 'rgba(212,175,94,0.18)')
        : 'transparent',
      color: active ? IVORY : 'rgba(248,232,200,0.65)',
      fontFamily: 'Georgia, serif', fontStyle: 'italic',
      cursor: 'pointer', fontSize: 12,
    }}
  >{children}</button>
);

const Action = ({ onClick, children, testId, disabled, danger }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    data-testid={testId}
    style={{
      padding: '8px 16px', borderRadius: 999,
      border: `1px solid ${danger ? 'rgba(224,57,42,0.6)' : 'rgba(212,175,94,0.35)'}`,
      background: danger ? 'rgba(224,57,42,0.10)' : 'rgba(212,175,94,0.10)',
      color: danger ? '#e0392a' : IVORY,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.4 : 1,
      fontFamily: 'Georgia, serif', fontStyle: 'italic',
      letterSpacing: '0.04em', fontSize: 12,
    }}
  >{children}</button>
);

const Row = ({ k, v, testId }) => (
  <div style={{
    display: 'grid', gridTemplateColumns: '160px 1fr',
    gap: 12, padding: '3px 0',
    fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 11,
    color: IVORY, borderBottom: '1px solid rgba(212,175,94,0.06)',
  }}>
    <span style={{ color: IVORY_FADED }}>{k}</span>
    <span data-testid={testId} style={{ textAlign: 'right' }}>{String(v)}</span>
  </div>
);

const FailureCapturePage = () => {
  const [cameraProfile, setCameraProfile] = useState('guestbook');
  const [audioProfile, setAudioProfile] = useState('voice_message');
  const [failureClass, setFailureClass] = useState(FAILURE_CLASS_LIST[0]);
  const [severity, setSeverity] = useState('major');
  const [reproductionSteps, setReproductionSteps] = useState('');
  const [operatorNotes, setOperatorNotes] = useState('');
  const [capturedBy, setCapturedBy] = useState(() => {
    try { return localStorage.getItem('eventdrop.failure.capturedBy') || ''; }
    catch { return ''; }
  });
  const [rehearsalPackId, setRehearsalPackId] = useState(null);
  const [luminance, setLuminance] = useState(null);
  const [peakTrace, setPeakTrace] = useState([]);
  const [conditions, setConditions] = useState(null);
  const [failures, setFailures] = useState(() => listFailures());
  const [classification, setClassification] = useState([]);
  const [statusMsg, setStatusMsg] = useState('idle');

  const cam = useBoothCameraPipeline({ profile: cameraProfile });
  const mic = useParticipationAudioPipeline({ profile: audioProfile });

  // Live luminance sampler — every 600ms while the camera is
  // live, draws the current frame into a hidden canvas and runs
  // detectLuminance(). Cheap, single-pass.
  useEffect(() => {
    if (cam.status !== 'live') return undefined;
    const id = setInterval(() => {
      const v = cam.videoRef.current;
      if (!v || !v.videoWidth) return;
      const canvas = document.createElement('canvas');
      canvas.width = v.videoWidth;
      canvas.height = v.videoHeight;
      canvas.getContext('2d').drawImage(v, 0, 0);
      setLuminance(detectLuminance(canvas));
    }, 600);
    return () => clearInterval(id);
  }, [cam.status, cam.videoRef]);

  // Rolling peak trace for mic silence detection — last ~12s.
  useEffect(() => {
    if (mic.status !== 'live' && mic.status !== 'recording') return undefined;
    const id = setInterval(() => {
      setPeakTrace((arr) => {
        const next = [...arr, mic.peakLevel];
        if (next.length > 60) next.shift();
        return next;
      });
    }, 200);
    return () => clearInterval(id);
  }, [mic.status, mic.peakLevel]);

  // Environmental conditions snapshot — refreshed on Acquire.
  const refreshConditions = async () => {
    setConditions(await captureSoakConditions());
  };

  // Persist operator alias across reloads so a long rehearsal
  // session doesn't keep prompting for the same initials.
  useEffect(() => {
    try { localStorage.setItem('eventdrop.failure.capturedBy', capturedBy); }
    catch { /* incognito */ }
  }, [capturedBy]);

  // Apply a rehearsal pack — sets profiles, failure class, severity
  // and PRE-FILLS reproductionSteps. The operator is expected to
  // edit before capturing (Craig: never make the pack fully
  // automatic — human observational context preserves nuance).
  const applyPack = (pack) => {
    if (pack.camera) setCameraProfile(pack.camera);
    if (pack.audio) setAudioProfile(pack.audio);
    setFailureClass(pack.failureClass);
    setSeverity(pack.severity);
    setReproductionSteps(pack.reproductionSteps);
    setRehearsalPackId(pack.id);
    setStatusMsg(`pack applied: ${pack.id}`);
  };

  const signature = useMemo(() => buildSignature({
    luminance,
    voicePresenceMoments: mic.voicePresenceMoments,
    rawRoomToneRMS: mic.rawRoomToneRMS,
    peakLevelTrace: peakTrace,
    counters: soakSnapshot().counters,
    conditions,
    cameraStatus: cam.status,
    audioStatus: mic.status,
    errors: {
      cameraMessage: cam.error,
      audioMessage: mic.error,
    },
  }), [luminance, mic.voicePresenceMoments, mic.rawRoomToneRMS, peakTrace, conditions, cam.status, cam.error, mic.status, mic.error]);

  const acquire = async () => {
    setStatusMsg('acquiring');
    setPeakTrace([]);
    setLuminance(null);
    await refreshConditions();
    await cam.start();
    await mic.start();
    setStatusMsg('live');
  };

  const release = () => {
    cam.stop();
    mic.stop();
    setStatusMsg('released');
  };

  const captureNow = () => {
    setStatusMsg('capturing');
    // Snapshot the current frame as evidence so the bundle is
    // self-contained even after the camera releases.
    const v = cam.videoRef.current;
    let framePngDataUrl = null;
    if (v && v.videoWidth) {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = Math.min(640, v.videoWidth);
        canvas.height = Math.round((canvas.width / v.videoWidth) * v.videoHeight);
        canvas.getContext('2d').drawImage(v, 0, 0, canvas.width, canvas.height);
        framePngDataUrl = canvas.toDataURL('image/jpeg', 0.7);
      } catch { /* cross-origin or hidden — fine */ }
    }
    const { bundle } = saveFailure({
      failureClass,
      severity,
      profileName: cameraProfile,
      profileConfigSnapshot: cam.profile,
      conditions,
      signature,
      reproductionSteps,
      operatorNotes,
      capturedBy,
      rehearsalPackId,
      evidence: { framePngDataUrl, audioProfile },
    });
    setFailures((arr) => [...arr, { key: bundle.failureClass + '@' + bundle.capturedAt, bundle }]);
    setStatusMsg('captured');
    setReproductionSteps('');
    setOperatorNotes('');
    // Refresh from storage so the listing is canonical.
    setTimeout(() => setFailures(listFailures()), 0);
  };

  const runClassifier = () => {
    setClassification(classifyFailure(signature));
  };

  const removeFailure = (key) => {
    deleteFailure(key);
    setFailures(listFailures());
  };

  return (
    <div
      data-testid="failure-capture-page"
      style={{
        minHeight: '100vh', background: '#08070a', color: IVORY,
        padding: '40px 28px 80px',
        fontFamily: 'Inter, system-ui, sans-serif',
      }}
    >
      <header style={{ maxWidth: 1180, margin: '0 auto 24px' }}>
        <p style={{
          fontFamily: 'Georgia, serif', fontStyle: 'italic',
          color: IVORY_FADED, margin: 0,
          letterSpacing: '0.08em', textTransform: 'uppercase', fontSize: 12,
        }}>EventDrop ✦ Internal</p>
        <h1 style={{
          fontFamily: 'Georgia, "Cormorant Garamond", serif',
          fontWeight: 500, fontSize: 36, margin: '4px 0 6px', color: IVORY,
        }}>Failure capture</h1>
        <p style={{ margin: 0, color: IVORY_FADED, fontSize: 14, maxWidth: 900 }}>
          Operational failure intelligence. Provoke a known archetype, watch the
          passive detectors, annotate the human context, capture the canonical
          signature. Rule-based classification (no opaque ML). All storage is
          local; export bundles for archive.
        </p>
      </header>

      <main style={{ maxWidth: 1180, margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 28 }}>
        {/* LEFT — controls + preview */}
        <section style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div style={{ padding: 18, border: GOLD_BORDER, borderRadius: 14, background: PANEL_BG }} data-testid="failure-controls">
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <Action testId="failure-acquire" onClick={acquire} disabled={cam.status === 'live'}>
                Acquire ✦
              </Action>
              <Action testId="failure-release" onClick={release} disabled={cam.status !== 'live' && mic.status !== 'live'}>
                Release
              </Action>
              <Action testId="failure-capture" onClick={captureNow} disabled={cam.status !== 'live'}>
                Capture failure ✦
              </Action>
              <Action testId="failure-classify" onClick={runClassifier} disabled={cam.status !== 'live'}>
                Classify current state ✦
              </Action>
              <span data-testid="failure-status" style={{ marginLeft: 'auto', color: IVORY_FADED, fontSize: 12 }}>{statusMsg}</span>
            </div>
          </div>

          <div style={{ padding: 18, border: GOLD_BORDER, borderRadius: 14, background: PANEL_BG }} data-testid="failure-rehearsal-packs">
            <h3 style={{ margin: '0 0 8px', color: '#c19a4b', fontFamily: 'Georgia, serif', fontStyle: 'italic', fontWeight: 500, fontSize: 18 }}>
              rehearsal packs ✦
            </h3>
            <p style={{ margin: '0 0 10px', color: IVORY_FADED, fontSize: 12 }}>
              one-tap canonical operational scenarios. profile + class + severity get pre-filled; reproductionSteps becomes editable — edit it with your real observations before capturing.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {FAILURE_REHEARSAL_PACK_LIST.map((pack) => (
                <button
                  key={pack.id}
                  type="button"
                  onClick={() => applyPack(pack)}
                  data-testid={`failure-pack-${pack.id}`}
                  title={pack.description}
                  style={{
                    padding: '6px 12px', borderRadius: 999,
                    border: '1px solid rgba(212,175,94,0.35)',
                    background: rehearsalPackId === pack.id ? 'rgba(212,175,94,0.18)' : 'transparent',
                    color: rehearsalPackId === pack.id ? IVORY : 'rgba(248,232,200,0.65)',
                    fontFamily: 'Georgia, serif', fontStyle: 'italic',
                    cursor: 'pointer', fontSize: 12,
                  }}
                >{pack.title}</button>
              ))}
            </div>
            {rehearsalPackId && (() => {
              const pack = FAILURE_REHEARSAL_PACK_LIST.find((p) => p.id === rehearsalPackId);
              if (!pack) return null;
              return (
                <div style={{ marginTop: 10, padding: 10, borderRadius: 8, background: 'rgba(212,175,94,0.04)' }} data-testid={`failure-pack-expected-${pack.id}`}>
                  <div style={{ color: IVORY_FADED, fontSize: 11, fontFamily: 'ui-monospace, Menlo, monospace', marginBottom: 4 }}>
                    expectedSignals ✦ (operator guidance — not enforced)
                  </div>
                  {pack.expectedSignals.map((s) => (
                    <div key={s} style={{ color: IVORY, fontSize: 11, fontFamily: 'ui-monospace, Menlo, monospace' }}>· {s}</div>
                  ))}
                </div>
              );
            })()}
          </div>

          <div style={{ padding: 18, border: GOLD_BORDER, borderRadius: 14, background: PANEL_BG }}>
            <h3 style={{ margin: '0 0 8px', color: '#c19a4b', fontFamily: 'Georgia, serif', fontStyle: 'italic', fontWeight: 500, fontSize: 18 }}>
              failure archetype ✦
            </h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }} data-testid="failure-class-picker">
              {FAILURE_CLASS_LIST.map((c) => (
                <Pill
                  key={c}
                  active={c === failureClass}
                  onClick={() => setFailureClass(c)}
                  testId={`failure-class-${c}`}
                  tone="danger"
                >{c}</Pill>
              ))}
            </div>
            <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 6 }} data-testid="failure-severity-picker">
              <span style={{ color: IVORY_FADED, fontSize: 12, alignSelf: 'center', marginRight: 8, fontFamily: 'ui-monospace, Menlo, monospace' }}>severity</span>
              {SEVERITY_LIST.map((s) => (
                <Pill key={s} active={s === severity} onClick={() => setSeverity(s)} testId={`failure-severity-${s}`}>
                  {s}
                </Pill>
              ))}
            </div>
          </div>

          <div style={{ padding: 18, border: GOLD_BORDER, borderRadius: 14, background: PANEL_BG }}>
            <h3 style={{ margin: '0 0 8px', color: '#c19a4b', fontFamily: 'Georgia, serif', fontStyle: 'italic', fontWeight: 500, fontSize: 18 }}>
              profiles ✦
            </h3>
            <div style={{ marginBottom: 10, color: IVORY_FADED, fontSize: 12, fontFamily: 'ui-monospace, Menlo, monospace' }}>camera</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {CAMERA_PROFILES.map((p) => (
                <Pill key={p} active={p === cameraProfile} onClick={() => setCameraProfile(p)} testId={`failure-cam-${p}`}>
                  {p}
                </Pill>
              ))}
            </div>
            <div style={{ margin: '10px 0', color: IVORY_FADED, fontSize: 12, fontFamily: 'ui-monospace, Menlo, monospace' }}>audio</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {AUDIO_PROFILES.map((p) => (
                <Pill key={p} active={p === audioProfile} onClick={() => setAudioProfile(p)} testId={`failure-mic-${p}`}>
                  {p}
                </Pill>
              ))}
            </div>
          </div>

          <div style={{
            position: 'relative', aspectRatio: String(cam.profile.aspectRatio),
            background: '#000', borderRadius: 10, overflow: 'hidden', border: GOLD_BORDER,
          }}>
            <video ref={cam.videoRef} playsInline muted style={cam.previewStyle} data-testid="failure-video-preview" />
          </div>

          <div style={{ padding: 18, border: GOLD_BORDER, borderRadius: 14, background: PANEL_BG }}>
            <h3 style={{ margin: '0 0 8px', color: '#c19a4b', fontFamily: 'Georgia, serif', fontStyle: 'italic', fontWeight: 500, fontSize: 18 }}>
              operator context ✦
            </h3>
            <label style={{ display: 'block', color: IVORY_FADED, fontSize: 11, fontFamily: 'ui-monospace, Menlo, monospace', marginBottom: 4 }}>
              capturedBy (operator alias / initials)
            </label>
            <input
              data-testid="failure-captured-by"
              type="text"
              value={capturedBy}
              onChange={(e) => setCapturedBy(e.target.value)}
              placeholder="e.g. CW"
              style={{
                width: '100%', boxSizing: 'border-box',
                padding: 10, borderRadius: 8,
                background: 'rgba(0,0,0,0.4)',
                border: '1px solid rgba(212,175,94,0.25)',
                color: IVORY, fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12,
              }}
            />
            <label style={{ display: 'block', color: IVORY_FADED, fontSize: 11, fontFamily: 'ui-monospace, Menlo, monospace', marginTop: 10, marginBottom: 4 }}>
              reproductionSteps
            </label>
            <textarea
              data-testid="failure-reproduction-steps"
              rows={3}
              value={reproductionSteps}
              onChange={(e) => setReproductionSteps(e.target.value)}
              placeholder={'e.g. "covered lens with finger for 3s while recording"'}
              style={{
                width: '100%', boxSizing: 'border-box',
                padding: 10, borderRadius: 8,
                background: 'rgba(0,0,0,0.4)',
                border: '1px solid rgba(212,175,94,0.25)',
                color: IVORY, fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12,
                resize: 'vertical',
              }}
            />
            <label style={{ display: 'block', color: IVORY_FADED, fontSize: 11, fontFamily: 'ui-monospace, Menlo, monospace', marginTop: 10, marginBottom: 4 }}>
              operatorNotes
            </label>
            <textarea
              data-testid="failure-operator-notes"
              rows={3}
              value={operatorNotes}
              onChange={(e) => setOperatorNotes(e.target.value)}
              placeholder={'e.g. "Android kiosk rotated while charger inserted"'}
              style={{
                width: '100%', boxSizing: 'border-box',
                padding: 10, borderRadius: 8,
                background: 'rgba(0,0,0,0.4)',
                border: '1px solid rgba(212,175,94,0.25)',
                color: IVORY, fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12,
                resize: 'vertical',
              }}
            />
          </div>
        </section>

        {/* RIGHT — passive detectors + classification + library */}
        <section style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div style={{ padding: 18, border: GOLD_BORDER, borderRadius: 14, background: PANEL_BG }} data-testid="failure-archaeology">
            <h3 style={{ margin: '0 0 8px', color: '#c19a4b', fontFamily: 'Georgia, serif', fontStyle: 'italic', fontWeight: 500, fontSize: 18 }}>
              archaeology ✦
            </h3>
            {(() => {
              const a = archaeology();
              return (
                <>
                  <Row k="releaseChannel" v={a.releaseChannel} testId="failure-archaeology-release-channel" />
                  <Row k="buildSha" v={a.buildSha} testId="failure-archaeology-build-sha" />
                  <Row k="browserEngine" v={a.browserEngine} testId="failure-archaeology-browser-engine" />
                  <Row k="pipelineVersion" v={PIPELINE_VERSION} />
                  <Row k="deviceFingerprint" v={deviceFingerprint()} />
                </>
              );
            })()}
          </div>

          <div style={{ padding: 18, border: GOLD_BORDER, borderRadius: 14, background: PANEL_BG }} data-testid="failure-detectors">
            <h3 style={{ margin: '0 0 8px', color: '#c19a4b', fontFamily: 'Georgia, serif', fontStyle: 'italic', fontWeight: 500, fontSize: 18 }}>
              passive detectors ✦
            </h3>
            <Row k="luminance.mean" v={luminance ? luminance.mean.toFixed(1) : '—'} testId="failure-detector-luminance-mean" />
            <Row k="luminance.variance" v={luminance ? luminance.variance.toFixed(1) : '—'} testId="failure-detector-luminance-variance" />
            <Row k="luminance.clipped%" v={luminance ? luminance.clippedPct.toFixed(1) : '—'} testId="failure-detector-luminance-clipped" />
            <Row k="luminance.dark%" v={luminance ? luminance.darkPct.toFixed(1) : '—'} testId="failure-detector-luminance-dark" />
            <Row k="flags.underexposed" v={signature.flags.underexposed ? 'YES' : 'no'} testId="failure-detector-flag-underexposed" />
            <Row k="flags.overexposed" v={signature.flags.overexposed ? 'YES' : 'no'} testId="failure-detector-flag-overexposed" />
            <Row k="flags.lensObstructed" v={signature.flags.lensObstructed ? 'YES' : 'no'} testId="failure-detector-flag-lensobstructed" />
            <Row k="flags.micSilent" v={signature.flags.micSilent ? 'YES' : 'no'} testId="failure-detector-flag-micsilent" />
            <Row k="rawRoomToneRMS" v={signature.rawRoomToneRMS?.toFixed?.(4) || 0} />
            <Row k="peakMax" v={signature.peakMax?.toFixed?.(4) || 0} />
            <Row k="voiceMomentCount" v={signature.voiceMomentCount} />
            <Row k="cameraStatus" v={signature.cameraStatus || '—'} />
            <Row k="audioStatus" v={signature.audioStatus || '—'} />
          </div>

          <div style={{ padding: 18, border: GOLD_BORDER, borderRadius: 14, background: PANEL_BG }} data-testid="failure-classifier-output">
            <h3 style={{ margin: '0 0 8px', color: '#c19a4b', fontFamily: 'Georgia, serif', fontStyle: 'italic', fontWeight: 500, fontSize: 18 }}>
              classification ✦
            </h3>
            {classification.length === 0 ? (
              <p style={{ margin: 0, color: IVORY_FADED, fontSize: 12 }}>
                tap “Classify current state ✦” to score the live signature against the rule-based classifier.
              </p>
            ) : (
              classification.map((c) => (
                <div key={c.class} style={{
                  padding: 8, borderBottom: '1px solid rgba(212,175,94,0.06)',
                  display: 'grid', gridTemplateColumns: '160px 60px 1fr', gap: 10,
                  fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 11,
                  color: IVORY,
                }} data-testid={`failure-classified-${c.class}`}>
                  <span>{c.class}</span>
                  <span style={{ color: c.confidence > 0.7 ? '#c19a4b' : IVORY_FADED }}>
                    {(c.confidence * 100).toFixed(0)}%
                  </span>
                  <span style={{ color: IVORY_FADED }}>{c.why}</span>
                </div>
              ))
            )}
          </div>

          <div style={{ padding: 18, border: GOLD_BORDER, borderRadius: 14, background: PANEL_BG }} data-testid="failure-library">
            <h3 style={{ margin: '0 0 8px', color: '#c19a4b', fontFamily: 'Georgia, serif', fontStyle: 'italic', fontWeight: 500, fontSize: 18 }}>
              archetype library ✦ <span style={{ color: IVORY_FADED, fontSize: 11, fontFamily: 'ui-monospace, Menlo, monospace' }}>
                device {deviceFingerprint()} · pipeline {PIPELINE_VERSION}
              </span>
            </h3>
            {failures.length === 0 ? (
              <p style={{ margin: 0, color: IVORY_FADED, fontSize: 12 }}>
                no captured failures yet. acquire a profile, induce the failure condition, then tap “Capture failure ✦”.
              </p>
            ) : (
              failures.map(({ key, bundle }) => (
                <div key={key} style={{
                  padding: 10, borderBottom: '1px solid rgba(212,175,94,0.08)',
                  fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 11,
                  color: IVORY,
                }} data-testid={`failure-library-row`}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 10, alignItems: 'center' }}>
                    <div>
                      <div>
                        <span style={{ color: '#c19a4b' }}>{bundle.failureClass}</span>
                        {' · '}
                        <span style={{ color: IVORY_FADED }}>{bundle.severity}</span>
                        {' · '}
                        <span style={{ color: IVORY_FADED }}>{bundle.profileName}</span>
                      </div>
                      <div style={{ color: IVORY_FADED, fontSize: 10 }}>{bundle.capturedAt}</div>
                      {bundle.operatorNotes && (
                        <div style={{ color: IVORY_FADED, fontSize: 10, marginTop: 2 }}>
                          ✎ {bundle.operatorNotes}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <Action testId={`failure-library-export-${bundle.failureClass}`} onClick={() => downloadGolden(bundle)}>
                        Export
                      </Action>
                      <Action testId={`failure-library-delete-${bundle.failureClass}`} onClick={() => removeFailure(key)} danger>
                        Delete
                      </Action>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </section>
      </main>
    </div>
  );
};

export default FailureCapturePage;
